
export class Color {
  public static darkenHexColor(hex: string, percent: number) {
    // Validate input
    if (percent < 0 || percent > 100) {
      throw new Error('Percentage must be between 0 and 100');
    }

    // Remove # if present
    hex = hex.replace('#', '').substring(0, 6);

    // Validate hex format
    if (!/^[0-9A-F]{6}$/i.test(hex)) {
      throw new Error(`Invalid hex color format: ${hex}`);
    }

    // Convert hex to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate darker RGB values
    const darkenFactor = 1 - (percent / 100);
    const darkenedR = Math.max(0, Math.floor(r * darkenFactor));
    const darkenedG = Math.max(0, Math.floor(g * darkenFactor));
    const darkenedB = Math.max(0, Math.floor(b * darkenFactor));

    // Convert back to hex
    const toHex = (c: number) => {
      const hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(darkenedR)}${toHex(darkenedG)}${toHex(darkenedB)}`;
  }

  public static lightenHexColor(hex: string, percent: number) {
    // Remove # if present
    hex = hex.replace(/^#/, '');

    // Parse r, g, b values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate lightened values
    const lighten = (color: number) => {
      return Math.min(255, color + (255 - color) * (percent / 100));
    };

    // Apply lightening to each channel
    const newR = Math.round(lighten(r));
    const newG = Math.round(lighten(g));
    const newB = Math.round(lighten(b));

    // Convert back to hex
    const toHex = (value: number) => {
      const hex = value.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
  }

  public static letterToColorfulHex(input: string): string {
    if (!input || input.length === 0) return '#5a5a5a'; // Medium gray for empty input

    // Hash calculation for more even distribution
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      hash = input.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Extract RGB components from the hash
    const r = (hash & 0xFF0000) >> 16;
    const g = (hash & 0x00FF00) >> 8;
    const b = hash & 0x0000FF;

    // Boost saturation for more vibrant colors
    const boostedR = Math.min(255, r + 50);
    const boostedG = Math.min(255, g + 50);
    const boostedB = Math.min(255, b + 50);

    // Convert to hex and ensure 2 digits per component
    const toHex = (c: number) => c.toString(16).padStart(2, '0');
    return `#${toHex(boostedR)}${toHex(boostedG)}${toHex(boostedB)}`;
  }
}
